import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  NgModule,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges
} from '@angular/core';
import {CommonModule, isPlatformServer} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {Resource} from '@madeinlune/ngrx-json-api/src/interfaces';
import {Observable, of, ReplaySubject} from 'rxjs';
import {debounce, delay, take} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {QuantityService} from './quantity.service';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {SnipcartService} from '../../snipcart/snipcart.service';
import {CART_QUANTITY_DEBOUNCE_TIME, DURATION_PROVIDERS} from '../../providers/duration.providers';
import {Variant} from '../../model/product';
import {TranslocoModule} from '@ngneat/transloco';
import {BOUTIQUE_CLOSED_MODE, BoutiqueClosedMode, IS_BOUTIQUE_CLOSED} from '../../providers/storeFront.providers';
import {WindowService} from '../../window/window.service';
import {isScullyRunning} from "@scullyio/ng-lib";

export interface QuantityEvent {
  variation: Resource;
  quantity: number;
}

@UntilDestroy()
@Component({
  selector: 'app-quantity',
  templateUrl: './quantity.component.html',
  styleUrls: ['./quantity.component.scss'],
  providers: [QuantityService, DURATION_PROVIDERS],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuantityComponent implements OnInit, OnDestroy, OnChanges {

  internalQuantity: number;

  cartQuantity: number;

  @Input()
  set quantity(value: number) {
    this.cartQuantity = value;
    this.internalQuantity = value;
  }

  @Input()
  variation: Variant;

  @Input()
  hasMoreVariants: boolean;

  addReplay$: ReplaySubject<number> = new ReplaySubject<number>(1);

  removeReplay$: ReplaySubject<number> = new ReplaySubject<number>(1);

  isServer: boolean = isPlatformServer(this.platformId);

  scullyRunning$: Observable<boolean> = of(isScullyRunning());

  constructor(
    public quantityService: QuantityService,
    public snipcartService: SnipcartService,
    @Inject(CART_QUANTITY_DEBOUNCE_TIME) private quantityDebounceTime: number,
    @Inject(IS_BOUTIQUE_CLOSED) public readonly isBoutiqueClosed$: Observable<boolean>,
    @Inject(BOUTIQUE_CLOSED_MODE) public readonly boutiqueClosedMode$: Observable<BoutiqueClosedMode>,
    @Inject(PLATFORM_ID) public readonly platformId: any,
    private windowService: WindowService
  ) {
  }

  ngOnDestroy(): void {
  }

  ngOnInit(): void {

    this.addReplay$
      .pipe(
        debounce(val => of(true).pipe(delay(val <= 1 ? 0 : this.quantityDebounceTime))),
        untilDestroyed(this)
      )
      .subscribe(newQuantity => {
        this.quantityService.addVariantQuantity(newQuantity);
      });

    this.removeReplay$
      .pipe(
        debounce(val => of(true).pipe(delay(val === 0 ? 0 : this.quantityDebounceTime))),
        untilDestroyed(this)
      )
      .subscribe(newQuantity => {
        this.quantityService.removeVariantQuantity(newQuantity);
      });

  }

  add() {
    this.isBoutiqueClosed$.pipe(take(1)).subscribe((isBoutiqueClosed) => {
      if (!isBoutiqueClosed) {
        this.internalQuantity = this.internalQuantity + 1;
        this.addReplay$.next(this.internalQuantity);
      } else {
        this.displayBoutiqueClosed();
      }
    });
  }

  remove() {
    this.isBoutiqueClosed$.pipe(take(1)).subscribe((isBoutiqueClosed) => {
      if (!isBoutiqueClosed) {
        this.internalQuantity = this.internalQuantity - 1;
        this.internalQuantity = Math.max(0, this.internalQuantity);
        this.removeReplay$.next(this.internalQuantity);
      } else {
        this.displayBoutiqueClosed();
      }
    });
  }

  displayBoutiqueClosed() {
    this.windowService.openAlert(
      null,
      'Désolé, notre boutique en ligne est fermée.',
      [
        {
          id: 'boutiqueClosed',
          action: {type: 'test', data: null},
          color: 'accent',
          label: 'OK'
        }
      ]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.variation?.previousValue?.id !== changes.variation?.currentValue?.id) {
      this.quantityService.variation = this.variation;
    }
  }

}

@UntilDestroy()
@NgModule({
  declarations: [
    QuantityComponent
  ],
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslocoModule
  ],
  exports: [
    QuantityComponent
  ],
  entryComponents: []
})

export class QuantityModule {
}
